import React, {useContext, useEffect, useState} from 'react';
import {IListConfigRelationColumn} from '@src/@interfaces/components/List/IListConfigRelationColumn';
import {ListConfigNamedColumn} from '@src/@types/components/List';
import {IListColumnProps} from '@src/@interfaces/components/List/IListColumnProps';
import {IOrderModel} from '@src/@interfaces/models/Order/IOrderModel';
import {MomentJs, parseDate} from '@src/helpers/dates';
import CountDown from '@src/components/Timer/CountDown';
import DatetimeColumn from '@src/components/List/columns/DatetimeColumn';
import {LocaleContext} from '@src/contexts/LocaleContext';
import {DateTime} from '@src/@types/DateTime';
import {BootstrapColors} from '@src/@types/Bootstrap';

export const getProductWithNearestDeadline = (
        products: IOrderModel['products'],
        filter?: (item: Required<IOrderModel>['products'][number]) => boolean,
) => {
    return products?.filter(product => {
        return product.deadline_at && (!filter || filter(product));
    }).sort((a, b) => {
        return +parseDate(a.deadline_at) - +parseDate(b.deadline_at);
    }).at(0);
};

export function DeadlineCounter({date, redColorSince, yellowColorSince, ...props}: {
    date?: MomentJs,
    redColorSince?: DateTime | null,
    yellowColorSince?: DateTime | null,
} & Omit<React.ComponentProps<typeof CountDown>, 'expiredAt'>) {
    if (!date) {
        return null;
    }

    let color: BootstrapColors | null = null;
    const shouldPulse = +date.toDate() < Date.now();

    [redColorSince, yellowColorSince].some((since, i) => {
        const sinceDate = since ? parseDate(since) : undefined;
        if (sinceDate && +sinceDate <= Date.now()) {
            color = i ? 'warning' : 'danger';

            return true;
        }

        return false;
    });

    if (!color) {
        const diff = date.diff(new Date(), 'days');
        if (diff != null && diff >= 0 && diff <= 5) {
            color = diff > 2 ? 'warning' : 'danger';
        }
    }

    if (!color) {
        return null;
    }

    return <CountDown
            expiredAt={date}
            badgeColor={color}
            shouldPulse={shouldPulse}
            {...props}
    />;
}

export default function OrderListDeadlineAtColumn(
        props: IListColumnProps<ListConfigNamedColumn<IListConfigRelationColumn>, IOrderModel>
) {
    useContext(LocaleContext);
    const [, triggerRerender] = useState(Date.now());

    const orderStatus = props.model?.status;
    const isCompleted = orderStatus?.is_completed ?? orderStatus?.is_completed_without_payment;
    const orderDeadline = (!isCompleted && props.model?.deadline_at) ? parseDate(props.model?.deadline_at) : undefined;
    const productWithNearestDeadline = isCompleted ?
            undefined :
            getProductWithNearestDeadline(props.model?.products, product => (
                    !product.attention &&
                    (
                            !product.status?.is_completed ||
                            product.status?.approval_is_required === true &&
                            product.status_approval_state !== 'accepted'
                    )
            ));
    const nearestProductDeadline = productWithNearestDeadline?.deadline_at ?
            parseDate(productWithNearestDeadline.deadline_at) :
            undefined;

    const displayProductCounter = (() => {
        if (productWithNearestDeadline && ['red_indicator_color_since', 'yellow_indicator_color_since'].some(since => {
            return since != null && +parseDate(since) <= Date.now();
        })) {
            return true;
        }

        const diff = nearestProductDeadline?.diff(new Date(), 'days');

        return nearestProductDeadline != null && diff != null && diff >= 0 && diff <= 3;
    })();

    const isOrderDeadlineExpired = orderDeadline != null && +orderDeadline < Date.now();

    useEffect(() => {
        if (!orderDeadline || isOrderDeadlineExpired) {
            return;
        }

        const timer = setTimeout(
                () => triggerRerender(Date.now()),
                +orderDeadline - Date.now()
        );

        return () => clearTimeout(timer);
    }, [isOrderDeadlineExpired, orderDeadline]);

    let counter = null;
    if (isOrderDeadlineExpired) {
        return <DeadlineCounter
                date={orderDeadline}
                redColorSince={props.model?.red_indicator_color_since}
                yellowColorSince={props.model?.yellow_indicator_color_since}
                titlePrefix="OrdersIndex.time-to-order-deadline"
        />;
    } else if (displayProductCounter) {
        counter = <DeadlineCounter
                date={nearestProductDeadline}
                redColorSince={productWithNearestDeadline?.red_indicator_color_since}
                yellowColorSince={productWithNearestDeadline?.yellow_indicator_color_since}
                titlePrefix="OrdersIndex.time-to-nearest-order-product-deadline"
        />;
    }

    return <>
        <DatetimeColumn {...props}/>

        {counter}
    </>;
}
